import { IAddressSearch } from '~interfaces/IStoresInterfaces';
import { AddressSelectedUseCase } from '~use-cases/AddressSelectedUseCase';
import { HydrateStoresWithCountersUseCase } from '~use-cases/HydrateStoresWithCountersUseCase';
import {
    BuyOnlinePickupInStoreFetchDataStrategy,
    FindInStoreFetchDataStrategy
} from './StoresFetchDataStrategies';
import { PannedOnMapUseCase } from '~use-cases/PannedOnMapUseCase';
import { StoresRepository } from '~repositories/StoresRepository';
import { Config } from '~domain/entities/Config';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { LocationResetClickedUseCase } from '~use-cases/LocationResetClickedUseCase';
import { BopisLocationResetClickedUseCase } from '~use-cases/BopisUseCases/BopisLocationResetClickedUseCase';

export interface IMapPanPosition {
    latitude: number;
    longitude: number;
}

export interface IAddressSelectedStrategy {
    onAddressSelected(searchAddress: IAddressSearch): Promise<void>;
    onMapPan(center: IMapPanPosition): Promise<void>;
    onUpdateMapLocation(searchAddress: IAddressSearch): Promise<void>;
    onLocationReset(): Promise<void>;
}

export class StoreLocatorAddressSelectedStrategy implements IAddressSelectedStrategy {
    private storesRepository: StoresRepository;
    private config: Config;

    constructor() {
        this.storesRepository = diContainer.get(serviceNames.storesRepository);
        this.config = diContainer.get(serviceNames.config);
    }

    public onAddressSelected = async (searchAddress: IAddressSearch) => {
        await new AddressSelectedUseCase(searchAddress).execute();
        await new HydrateStoresWithCountersUseCase().execute();

        if (this.config.config.isOAB) {
            this.storesRepository.toOABOnlyStoresMode();
        }
    };

    public onUpdateMapLocation = async (searchAddress: IAddressSearch) => {
        await new AddressSelectedUseCase(searchAddress).execute();
        await new HydrateStoresWithCountersUseCase().execute();

        if (this.config.config.isOAB) {
            this.storesRepository.toOABOnlyStoresMode();
        }
    };

    public onMapPan = async (center: IMapPanPosition) => {
        await new PannedOnMapUseCase({
            centerLat: center.latitude,
            centerLng: center.longitude
        }).execute();
        await new HydrateStoresWithCountersUseCase().execute();

        if (this.config.config.isOAB) {
            this.storesRepository.toOABOnlyStoresMode();
        }
    };

    public onLocationReset = async () => {
        await new LocationResetClickedUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();

        if (this.config.config.isOAB) {
            this.storesRepository.toOABOnlyStoresMode();
        }
    };
}

export class FindInStoreAddressSelectedStrategy implements IAddressSelectedStrategy {
    public onAddressSelected = async (searchAddress: IAddressSearch) => {
        await new AddressSelectedUseCase(
            searchAddress,
            new FindInStoreFetchDataStrategy()
        ).execute();
    };

    public onUpdateMapLocation = async (searchAddress: IAddressSearch) => {
        await new AddressSelectedUseCase(
            searchAddress,
            new FindInStoreFetchDataStrategy()
        ).execute();
    };

    public onMapPan = async (center: IMapPanPosition) => {
        await new PannedOnMapUseCase(
            {
                centerLat: center.latitude,
                centerLng: center.longitude
            },
            new FindInStoreFetchDataStrategy()
        ).execute();
    };

    public onLocationReset = async () => {
        await new LocationResetClickedUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();
    };
}

export class BuyOnlinePickupInStoreAddressSelectedStrategy implements IAddressSelectedStrategy {
    public onAddressSelected = async (searchAddress: IAddressSearch) => {
        await new AddressSelectedUseCase(
            searchAddress,
            new BuyOnlinePickupInStoreFetchDataStrategy()
        ).execute();
    };

    public onUpdateMapLocation = async (searchAddress: IAddressSearch) => {
        await new AddressSelectedUseCase(
            searchAddress,
            new BuyOnlinePickupInStoreFetchDataStrategy()
        ).execute();
    };

    public onMapPan = async (center: IMapPanPosition) => {
        await new PannedOnMapUseCase(
            {
                centerLat: center.latitude,
                centerLng: center.longitude
            },
            new BuyOnlinePickupInStoreFetchDataStrategy()
        ).execute();
    };

    public onLocationReset = async () => {
        await new BopisLocationResetClickedUseCase().execute();
        await new HydrateStoresWithCountersUseCase().execute();
    };
}
